import React, { useEffect, useState } from "react";
import { Container, Spinner } from "react-bootstrap";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { getPageName } from "../../../locales/translationUtils";
import { buscarPainelExecutivo } from "../../../services/painelExecutivoService";
import { PainelExecutivo } from "../../../types/painelExecutivo";
import "./DashboardView.css";

const formatoNumero = (valor: number, casas: number = 0) =>
  valor.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });

const formatoMoeda = (valor: number) =>
  valor.toLocaleString("pt-BR", { style: "currency", currency: "USD" });

/**
 * Painel Executivo — os 10 indicadores calculados ao vivo a partir do banco (ver
 * PainelExecutivoController/PainelExecutivoDAO no back-end). Restrito a ROLE_MASTER.
 *
 * Protótipo aprovado para virar a tela padrão, se validado com a diretoria - ver
 * IniciativasEstrategicas para as 5 apostas estratégicas que motivaram este painel.
 */
const PainelExecutivoDashboardView: React.FC = () => {
  const [painel, setPainel] = useState<PainelExecutivo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const buscar = async () => {
      try {
        const resultado = await buscarPainelExecutivo();
        setPainel(resultado);
      } catch (e: any) {
        setErro(
          e?.response?.data?.messages?.join(" ") ||
          "Não foi possível calcular o painel executivo."
        );
      } finally {
        setCarregando(false);
      }
    };

    buscar();
  }, []);

  const navItems: NavBarItem[] = [
    { label: "Início", route: "/", iconName: "house-door" },
  ];

  return (
    <Container fluid style={{ display: "flex", flexDirection: "column" }}>
      <NavBar items={navItems} />
      <div className="mb-2"></div>

      <div className="painel-wrap">
        <div className="painel-header">
          <div>
            <h2>{getPageName("painelExecutivo")}</h2>
            <div className="painel-subtitle">
              10 indicadores calculados ao vivo, direto do banco — sem campo novo, sem integração externa.
            </div>
          </div>
          {painel && (
            <div className="painel-meta">
              calculado em {new Date(painel.calculadoEm).toLocaleString("pt-BR")}
            </div>
          )}
        </div>

        {carregando && (
          <div className="painel-loading">
            <Spinner animation="border" size="sm" className="me-2" />
            Calculando os indicadores...
          </div>
        )}

        {!carregando && erro && <div className="painel-error text-danger">{erro}</div>}

        {!carregando && !erro && painel && (
          <div className="kpi-grid">
            {/* KPI 1 — Lead time de liberação */}
            <div className="kpi-tile">
              <span className="kpi-num">01</span>
              <span className="kpi-label">Lead time de liberação</span>
              <div className="kpi-value">
                {painel.leadTimeMedioDias != null ? formatoNumero(painel.leadTimeMedioDias, 1) : "—"}
                <small>dias</small>
              </div>
              <div className="kpi-context">
                Média entre chegada no armazém e liberação — {formatoNumero(painel.leadTimeAmostras)} cargas com dados válidos.
              </div>
            </div>

            {/* KPI 2 — Peso e peças movimentadas */}
            <div className="kpi-tile">
              <span className="kpi-num">02</span>
              <span className="kpi-label">Peso e peças movimentadas</span>
              <div className="kpi-value">{formatoNumero(painel.pesoTotalKg / 1000, 1)}<small>ton</small></div>
              <div className="kpi-context">
                {formatoNumero(painel.pecasTotal)} peças, em {formatoNumero(painel.cargasConsideradas)} cargas.
                {painel.registrosExcluidosOutlier > 0 && (
                  <div className="kpi-outlier-note">
                    {painel.registrosExcluidosOutlier} registro(s) com peso fora do padrão excluído(s) do total.
                  </div>
                )}
              </div>
            </div>

            {/* KPI 6 — Mix modal */}
            <div className="kpi-tile">
              <span className="kpi-num">06</span>
              <span className="kpi-label">Mix modal</span>
              <div className="kpi-split-values">
                <span>✈️ Aéreo {formatoNumero(painel.percentualAereo, 2)}%</span>
                <span>🚢 Marítimo {formatoNumero(painel.percentualMaritimo, 2)}%</span>
              </div>
              <div className="kpi-bar-track">
                <div className="kpi-bar-fill" style={{ width: `${painel.percentualAereo}%` }}></div>
                <div className="kpi-bar-fill alt" style={{ width: `${painel.percentualMaritimo}%` }}></div>
              </div>
              <div className="kpi-context">{formatoNumero(painel.totalReleases)} liberações no total.</div>
            </div>

            {/* KPI 5 — Pendência documental */}
            <div className="kpi-tile">
              <span className="kpi-num">05</span>
              <span className="kpi-label">Pendência documental</span>
              <div className="kpi-value">{formatoNumero(painel.taxaSemOriginalPercentual, 1)}<small>% sem original</small></div>
              <div className="kpi-context">
                {formatoNumero(painel.taxaDiscrepanciaPercentual, 2)}% com discrepância registrada, sobre {formatoNumero(painel.totalReleases)} liberações.
              </div>
            </div>

            {/* KPI 7 — Concentração por cliente (wide) */}
            <div className="kpi-tile kpi-tile-wide">
              <span className="kpi-num">07</span>
              <span className="kpi-label">Concentração de volume por cliente</span>
              <div className="kpi-context" style={{ marginTop: 0 }}>
                Os 5 maiores respondem por {formatoNumero(painel.concentracaoTop5Percentual, 1)}% do peso movimentado.
              </div>
              <div className="kpi-rank-list">
                {painel.top5Clientes.map((c) => (
                  <div className="kpi-rank-row" key={c.nome}>
                    <div className="kpi-rank-top">
                      <span className="kpi-rank-name" title={c.nome}>{c.nome}</span>
                      <span className="kpi-rank-value">{formatoNumero(c.percentualDoTotal, 1)}%</span>
                    </div>
                    <div className="kpi-rank-track">
                      <div className="kpi-rank-fill" style={{ width: `${c.percentualDoTotal}%` }}></div>
                    </div>
                  </div>
                ))}
                {painel.top5Clientes.length === 0 && <div className="kpi-empty">Sem dados suficientes ainda.</div>}
              </div>
            </div>

            {/* KPI 8 — Aging de carga não liberada (wide) */}
            <div className="kpi-tile kpi-tile-wide">
              <span className="kpi-num">08</span>
              <span className="kpi-label">Aging de cargas não liberadas</span>
              <div className="kpi-context" style={{ marginTop: 0 }}>
                {formatoNumero(painel.totalCargaNaoLiberada)} cargas em aberto no momento do cálculo.
              </div>
              <div className="kpi-rank-list">
                {painel.agingFaixas.map((f) => {
                  const max = Math.max(...painel.agingFaixas.map((x) => x.quantidade), 1);
                  return (
                    <div className="kpi-aging-row" key={f.faixa}>
                      <span>{f.faixa}</span>
                      <div className="kpi-aging-track">
                        <div className="kpi-aging-fill" style={{ width: `${(f.quantidade / max) * 100}%` }}></div>
                      </div>
                      <span style={{ textAlign: "right", fontFamily: "var(--mono)" }}>{formatoNumero(f.quantidade)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* KPI 3 — Peso taxável vs. real */}
            <div className="kpi-tile">
              <span className="kpi-num">03</span>
              <span className="kpi-label">Peso taxável × real</span>
              {painel.cargasComDimensao > 0 ? (
                <>
                  <div className="kpi-split-values">
                    <span>Taxável {formatoNumero(painel.pesoTaxavelMedioKg, 1)}kg</span>
                    <span>Real {formatoNumero(painel.pesoRealMedioKg, 1)}kg</span>
                  </div>
                  <div className="kpi-context">
                    Médias por carga, {formatoNumero(painel.cargasComDimensao)} cargas com dimensão cadastrada. Fórmula IATA (C×L×A/6000).
                  </div>
                </>
              ) : (
                <div className="kpi-empty">Sem cargas com dimensão cadastrada ainda.</div>
              )}
            </div>

            {/* KPI 4 — Custo */}
            <div className="kpi-tile">
              <span className="kpi-num">04</span>
              <span className="kpi-label">Custo total e por kg</span>
              {painel.cargasComCusto > 0 ? (
                <>
                  <div className="kpi-value">{formatoMoeda(painel.custoTotal)}</div>
                  <div className="kpi-context">
                    {painel.custoPorKg != null ? `${formatoMoeda(painel.custoPorKg)}/kg — ` : ""}
                    {formatoNumero(painel.cargasComCusto)} cargas com custo lançado.
                  </div>
                </>
              ) : (
                <div className="kpi-empty">
                  Nenhum custo lançado por carga ainda — a infraestrutura já existe (tabela <code>cost</code>), só falta o time começar a lançar.
                </div>
              )}
            </div>

            {/* KPI 10 — Sucesso de e-mail */}
            <div className="kpi-tile">
              <span className="kpi-num">10</span>
              <span className="kpi-label">Sucesso de comunicação</span>
              <div className="kpi-value">{formatoNumero(painel.taxaSucessoEmailPercentual, 1)}<small>%</small></div>
              <div className="kpi-context">
                {formatoNumero(painel.totalEmails)} e-mails registrados no log até agora (módulo recente).
              </div>
            </div>

            {/* KPI 9 — Top liberadores (wide) */}
            <div className="kpi-tile kpi-tile-wide">
              <span className="kpi-num">09</span>
              <span className="kpi-label">Performance por liberador</span>
              <div className="kpi-rank-list">
                {painel.topLiberadores.map((l) => {
                  const max = Math.max(...painel.topLiberadores.map((x) => x.totalReleases), 1);
                  return (
                    <div className="kpi-rank-row" key={l.nome}>
                      <div className="kpi-rank-top">
                        <span className="kpi-rank-name" title={l.nome}>{l.nome}</span>
                        <span className="kpi-rank-value">{formatoNumero(l.totalReleases)} liberações</span>
                      </div>
                      <div className="kpi-rank-track">
                        <div className="kpi-rank-fill" style={{ width: `${(l.totalReleases / max) * 100}%` }}></div>
                      </div>
                    </div>
                  );
                })}
                {painel.topLiberadores.length === 0 && <div className="kpi-empty">Sem dados suficientes ainda.</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default PainelExecutivoDashboardView;
